import React, { ComponentType, ReactElement, useEffect } from 'react';
import { FiltersMap } from '@wix/bex-core';
import { observer } from 'mobx-react-lite';
import {
  CollectionTableWSRTable,
  CollectionTableWSRTableBaseProps,
  CollectionTableWSRTableProps,
} from '../CollectionTable/CollectionTableWSRTable';
import { CollectionTableBaseCommonProps } from '../CollectionTable/CollectionTableBaseCommonProps';
import { NestedTableItem, NestedTableState } from '../../state';
import { NestedTableFlatDataItem } from '../../state/NestedTableState/NestedTableFlatModeSectionState';
import { NestedTableContent } from './NestedTableContent';
import { TopNotificationBasicRenderProp } from '../TableTopNotification';
import { useToolbarCollectionSyncProps } from '../ToolbarCollection/useToolbarCollectionSyncProps';
import { useModeTableProps } from './useModeTableProps';
import { runInAction } from 'mobx';
import { QuerySearch } from '../QuerySearch';
import { MultiCollectionSupportProvider } from '../MultiCollectionSupportProvider';
import { Table as WSRTable } from '@wix/design-system';
import { TablePlaceholderStates } from '../TablePlaceholderStates/TablePlaceholderStates';
import { NestedTableDragAndDrop } from '../NestedTableDragAndDrop';

export interface NestedTableWSRTableBaseProps<
  C extends string,
  T,
  F extends FiltersMap,
> extends Omit<
      CollectionTableWSRTableBaseProps<
        NestedTableItem<C, any, any> | NestedTableFlatDataItem<C, any, any>,
        T,
        F
      >,
      | 'actionsCell'
      | 'sticky'
      | 'selectionDisabled'
      | 'renderEmptyState'
      | 'topNotification'
    >,
    CollectionTableBaseCommonProps<T, F> {}

export interface NestedTableWSRTableProps<
  C extends string,
  T,
  F extends FiltersMap,
> extends NestedTableWSRTableBaseProps<C, T, F> {
  dataHook?: string;
  renderEmptyState?: () => ReactElement;
  state: NestedTableState<C>;
  topNotification?: TopNotificationBasicRenderProp;

  rowHeight?: number;
  estimatedRowHeight?: number;
  dragAndDrop?: typeof NestedTableDragAndDrop;
}

function _NestedTableWSRTable<C extends string, T, F extends FiltersMap>(
  props: NestedTableWSRTableProps<C, T, F>,
) {
  const {
    state: wrapperState,
    renderError,
    multiLevelSorting,
    actionsCell,
    actionsCellWidth,
    actionsCellProps,
    filters,
    customColumns,
    views,
    renderEmptyState,
    topNotification,
    titleBarDisplay = true,
    horizontalScroll = true,
    rowHeight,
    estimatedRowHeight,
    rowVerticalPadding = 'tiny',
    dragAndDrop,
    dragAndDropBulkSubmit,
    dragAndDropSubmit,
    dragAndDropCancel,
    dragAndDropReorderModeToolbar,
    dragAndDropCategories,
    ...rest
  } = props;

  const { toolbar, query } = wrapperState;

  useToolbarCollectionSyncProps({
    ...props,
    state: toolbar,
    isUsingDragAndDrop: false,
    isUsingBulkActionsToolbar: false,
    isUsingHorizontalScroll: true,
    isUsingFolders: false,
    isUsingTableVirtualization: true,
  });

  const Table = CollectionTableWSRTable as ComponentType<
    CollectionTableWSRTableProps<any, T, F>
  >;

  const { state, key, BodyElementType, TrElementType, columns } =
    useModeTableProps({
      state: wrapperState,
      horizontalScroll,
      actionsCellWidth,
      actionsCellProps,
    });

  useEffect(() => {
    runInAction(() => {
      state.virtual.rowHeight = rowHeight;
    });
  }, [rowHeight, state]);

  useEffect(() => {
    if (estimatedRowHeight == null) {
      return;
    }
    runInAction(() => {
      state.virtual.estimatedRowHeight = estimatedRowHeight;
    });
  }, [estimatedRowHeight, state]);

  const { st, classes } =
    WSRTable.dataTableRowVirtualStyle as import('@stylable/runtime').RuntimeStylesheet;

  let table = (
    <Table
      rowVerticalPadding={rowVerticalPadding}
      search={<QuerySearch state={query.search} />}
      {...rest}
      showHeaderWhenEmpty
      sticky
      filters={filters}
      customColumns={customColumns}
      views={views}
      horizontalScroll={horizontalScroll}
      renderEmptyState={renderEmptyState}
      isApplyColumnWidthStyle
      useNewInfiniteScrollLoader
      titleBarDisplay={titleBarDisplay}
      rowClass={st(classes.root)}
      headerClass={st(classes.thead)}
      headerRowClass={st(classes.root)}
      TrElementType={TrElementType}
      state={state}
      BodyElementType={BodyElementType}
      columns={columns}
      dragAndDropReorderModeToolbar={dragAndDropReorderModeToolbar}
      dragAndDrop={dragAndDrop}
      dragAndDropCancel={dragAndDropCancel}
      dragAndDropSubmit={dragAndDropSubmit}
      dragAndDropBulkSubmit={dragAndDropBulkSubmit}
      dragAndDropCategories={dragAndDropCategories}
    >
      <MultiCollectionSupportProvider
        key={`MultiCollectionSupportProvider_${key}`}
        value={state.multi}
      >
        <TablePlaceholderStates
          key={`PlaceholderStates_${key}`}
          state={state}
          renderEmptyState={renderEmptyState}
          renderError={renderError}
          topNotification={topNotification}
          rowVerticalPadding={rowVerticalPadding}
        />
        <NestedTableContent key={`NestedTableContent_${key}`} state={state} />
      </MultiCollectionSupportProvider>
    </Table>
  );

  if (dragAndDrop) {
    const { CollectionNestedTableDragAndDropContext } = dragAndDrop;
    table = (
      <CollectionNestedTableDragAndDropContext
        state={wrapperState.nested}
        onSubmit={dragAndDropSubmit}
        onCancel={dragAndDropCancel}
        onBulkSubmit={dragAndDropBulkSubmit}
      >
        {table}
      </CollectionNestedTableDragAndDropContext>
    );
  }

  return table;
}

export const NestedTableWSRTable = observer(_NestedTableWSRTable);
